import React, { useEffect, useRef, useState } from "react";
import { Card, CircularProgress, Grid, Button } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import { login, clearLoading } from "../../redux/actions/LoginActions";
import { withStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import { useKeycloak } from "app/auth/KeycloakContext";

const styles = (theme) => ({
  wrapper: {
    position: "relative",
  },
  logoWrapper: {
    justifyContent: "space-around",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
});

const SignIn = ({ classes }) => {
  const { userInfo } = useKeycloak();
  const [loginUser, setUserLogin] = useState("");
  const [token, setToken] = useState("");
  const [force, setForce] = useState(null);
  const autoLoginTriggeredRef = useRef(false);

  const loginState = useSelector((state) => state.login);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clearLoading());
  }, [dispatch]);

  useEffect(() => {
    if (loginState.force !== force) {
      setForce(loginState.force);
    }
  }, [loginState.force, force]);

  useEffect(() => {
    if (userInfo && userInfo.username) {
      setUserLogin(userInfo.username);
      setToken(userInfo.token);
    }
  }, [userInfo]);

  // Auto-run LoadUserData when Keycloak token is ready (no button click needed)
  useEffect(() => {
    if (
      !userInfo?.username ||
      !userInfo?.token ||
      loginState.loading ||
      loginState.success ||
      autoLoginTriggeredRef.current
    ) {
      return;
    }
    autoLoginTriggeredRef.current = true;
    dispatch(login({ loginUser: userInfo.username, token: userInfo.token }));
  }, [userInfo?.username, userInfo?.token, loginState.loading, loginState.success, dispatch]);

  const handleFormSubmit = (event) => {
    event.preventDefault();
    dispatch(login({ loginUser, token }));
  };

  const errorMessage =
    loginState.error == null
      ? null
      : typeof loginState.error === "string"
        ? loginState.error
        : loginState.error?.message || loginState.error?.Message || "An error occurred. Please try again or refresh the page.";

  const error =
    !errorMessage || errorMessage === "Your session has expired." ? null : (
      <Alert variant="outlined" severity="error">
        {errorMessage}
      </Alert>
    );

  // Show minimal loading screen until we need to show the form (error/retry only)
  const showLoadingOnly =
    !userInfo?.token ||
    loginState.loading ||
    (autoLoginTriggeredRef.current && !loginState.success && !loginState.error);

  if (showLoadingOnly) {
    return (
      <div className="signin flex flex-center w-100 h-100vh">
        <div className="flex flex-center flex-middle flex-column">
          <CircularProgress size={48} />
          <span style={{ marginTop: 16 }}>Loading your employee info...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="signin flex flex-center w-100 h-100vh">
      <div className="p-8">
        <Card className="signin-card position-relative y-center">
          <Grid container>
            <Grid item lg={5} md={5} sm={5} xs={12}>
              <div
                className={
                  "p-32 flex flex-center flex-middle flex-column h-100 " +
                  classes.logoWrapper
                }
              >
                <img
                  style={{ maxWidth: 200 }}
                  width="200"
                  height="150"
                  src={`${process.env.REACT_APP_PUBLIC_URL}/assets/images/illustrations/dreamer.png`}
                  alt=""
                />
              </div>
            </Grid>
            <Grid item lg={7} md={7} sm={7} xs={12}>
              <div className="p-36 h-100 bg-light-gray position-relative">
                <span>
                  Please click the button below to load your employee info or retry after an error.
                </span>
                <ValidatorForm onSubmit={handleFormSubmit}>
                  <div className="flex flex-middle mb-8">
                    <div className={classes.wrapper}>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={loginState.loading}
                        type="submit"
                      >
                        Load data
                      </Button>
                      {loginState.loading && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </div>
                  </div>
                  {error}
                </ValidatorForm>
              </div>
            </Grid>
          </Grid>
        </Card>
      </div>
    </div>
  );
};
SignIn.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles, { withTheme: true })(withRouter(SignIn));
